import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAttribution } from '@/lib/attribution';
import { siteEvents } from '@/lib/siteEvents';
import './offer-slot.css';
import './newsletter-signup-card.css';

/**
 * Free signup for The Remote Accountant (remote accounting and finance jobs,
 * every weekday), in the places that used to carry a paid offer nobody clicked:
 * the job-page sidebar (job_rail_offer_v3: about 2,700 views and 0 clicks in
 * the 7 days to 2026-11-03) and the job-board footer column (about 10,400
 * views since 2026-10-20 and no clicks). The selling now happens in the
 * newsletter's welcome series, so these spots build the list instead.
 *
 * The submit writes a waitlist_interest row (intent 'newsletter', the anon
 * insert policy), which runs the standing double opt-in: waitlist-confirm
 * emails one confirmation link and newsletter-signup-sync subscribes the
 * address to The Remote Accountant once it is clicked. Events, surface in
 * props: newsletter_card_view (half on screen), newsletter_card_submit.
 * Hidden for the rest of the visit once this browser has signed up.
 */

const DONE_KEY = 'af_nl_optin:ra';

function alreadySignedUp(): boolean {
  try { return localStorage.getItem(DONE_KEY) === '1'; } catch { return false; }
}

export function NewsletterSignupCard({
  surface,
  variant = 'card',
}: {
  surface: 'job_rail' | 'footer';
  /** card: the bordered rail card; column: plain footer column. */
  variant?: 'card' | 'column';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [hidden] = useState(alreadySignedUp);
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const inputId = `nl-card-email-${surface}`;

  useEffect(() => {
    const el = ref.current;
    if (hidden || !el) return;
    const fire = () => siteEvents.act('newsletter_card_view', { surface, list: 'ra' });
    if (typeof IntersectionObserver === 'undefined') { fire(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { fire(); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [hidden, surface]);

  if (hidden) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'sending' || state === 'sent') return;
    const addr = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) { setState('error'); return; }
    setState('sending');
    const attribution = getAttribution();
    try {
      const { error } = await (supabase.from as unknown as (t: string) => {
        insert: (v: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
      })('waitlist_interest').insert({
        email: addr,
        source: `newsletter_card_${surface}`,
        intent: 'newsletter',
        context: {
          lists: ['ra'],
          surface,
          medium: 'job_board',
          campaign: `newsletter_card_${surface}`,
          tags: ['job-board', `card-${surface}`],
          path: typeof window !== 'undefined' ? window.location.pathname.slice(0, 200) : null,
          ...(attribution ? { attribution } : {}),
        },
      });
      if (error) throw new Error(error.message);
      try { localStorage.setItem(DONE_KEY, '1'); } catch { /* storage blocked */ }
      siteEvents.act('newsletter_card_submit', { surface, list: 'ra' });
      setState('sent');
    } catch (err) {
      console.error('newsletter card signup failed', err);
      setState('error');
    }
  };

  const body =
    state === 'sent'
      ? 'Check your inbox: one click in the email we just sent starts it. Nothing arrives until you click.'
      : 'The newest remote accounting and finance roles, with pay where the employer posts it. Free, unsubscribe in one click.';

  const form = state !== 'sent' && (
    <form className="nl-card__form" onSubmit={submit} noValidate>
      <label className="nl-card__label" htmlFor={inputId}>Email address</label>
      <input
        id={inputId}
        className="nl-card__input"
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@email.com"
        value={email}
        onChange={(ev) => { setEmail(ev.target.value); if (state === 'error') setState('idle'); }}
        aria-invalid={state === 'error'}
        required
      />
      <button type="submit" className="nl-card__btn" disabled={state === 'sending'}>
        {state === 'sending' ? 'Sending...' : 'Send me the jobs'}
      </button>
    </form>
  );

  if (variant === 'column') {
    return (
      <div ref={ref} className="footer__col nl-card nl-card--column" data-newsletter="ra">
        <h5>Remote jobs by email</h5>
        <p className="nl-card__body" aria-live="polite">{body}</p>
        {form}
        {state === 'error' && <p className="nl-card__err" role="alert">That did not go through. Check the address and try again.</p>}
      </div>
    );
  }

  return (
    <div ref={ref} className="af-offer af-offer--compact nl-card" data-newsletter="ra">
      <div className="af-offer__text">
        <span className="af-offer__kicker">Free newsletter</span>
        <h3 className="af-offer__headline">Remote accounting jobs, every weekday</h3>
        <p className="af-offer__body" aria-live="polite">{body}</p>
      </div>
      {form}
      {state === 'error' && <p className="nl-card__err" role="alert">That did not go through. Check the address and try again.</p>}
    </div>
  );
}

export default NewsletterSignupCard;
